import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

// Shapes follow the Bulk Actions API contract v1; the backend's /bulk routes implement the same.

/** An Ironclad stack the backend is configured for, e.g. Demo or Develop. */
export type BulkEnvironment = { id: string; name: string; baseUrl: string };

export type BulkArea =
  | 'workflows'
  | 'signatures'
  | 'records'
  | 'entities'
  | 'obligations'
  | 'users_groups';

/** A saved company. The Secret never comes back from the backend. */
export type BulkCompany = {
  id: string;
  environment: string;
  label: string;
  clientId: string;
  actingEmail: string;
  areas: BulkArea[];
  addedAt: string;
  lastUsedAt: string | null;
};

export type BulkSession = {
  startedAt: string;
  expiresAt: string;
  companies: BulkCompany[];
};

export type BulkSessionState = {
  environments: BulkEnvironment[];
  sessionHours: number;
  maxCompaniesPerRequest: number;
  /** Null when there's no session or it has expired. */
  session: BulkSession | null;
};

export type NewBulkCompany = {
  environment: string;
  label: string;
  clientId: string;
  clientSecret: string;
  actingEmail: string;
};

export type BulkVerifyErrorCode =
  | 'invalid_row'
  | 'invalid_credentials'
  | 'api_access_disabled'
  | 'not_a_member'
  | 'access_denied'
  | 'rate_limited'
  | 'ironclad_unavailable';

export type BulkCompanyResult =
  | { index: number; ok: true; company: BulkCompany; warnings: string[] }
  | {
      index: number;
      ok: false;
      error: { code: BulkVerifyErrorCode | string; message: string };
    };

export type AddCompaniesResult = {
  results: BulkCompanyResult[];
  session: BulkSession | null;
};

export class BulkActionsError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /**
     * The backend's code, or one of ours: `bulk_unavailable` also covers a backend with no
     * /bulk routes yet; `gateway`, `network` and `bad_response` are retryable transport issues.
     */
    readonly code: string,
    readonly retryable: boolean,
  ) {
    super(message);
    this.name = 'BulkActionsError';
  }
}

/** Bulk Actions isn't set up on this Backstage (no /bulk routes, or the backend turned it off). */
export const isBulkUnavailable = (error: unknown) =>
  error instanceof BulkActionsError && error.code === 'bulk_unavailable';

export interface BulkActionsApi {
  getSession(): Promise<BulkSessionState>;
  addCompanies(companies: NewBulkCompany[]): Promise<AddCompaniesResult>;
  removeCompany(id: string): Promise<{ session: BulkSession | null }>;
  endSession(): Promise<void>;
}

export const bulkActionsApiRef = createApiRef<BulkActionsApi>({
  id: 'plugin.armory.bulk-actions',
});

export class BulkActionsClient implements BulkActionsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getSession(): Promise<BulkSessionState> {
    return this.request('GET', '/session');
  }

  async addCompanies(companies: NewBulkCompany[]): Promise<AddCompaniesResult> {
    return this.request('POST', '/session/companies', { companies });
  }

  async removeCompany(id: string): Promise<{ session: BulkSession | null }> {
    return this.request(
      'DELETE',
      `/session/companies/${encodeURIComponent(id)}`,
    );
  }

  async endSession(): Promise<void> {
    await this.request('DELETE', '/session');
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    const url = `${await this.discoveryApi.getBaseUrl('armory')}/bulk${path}`;
    let res: Response;
    try {
      res = await this.fetchApi.fetch(url, {
        method,
        ...(body !== undefined && {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }),
      });
    } catch {
      throw new BulkActionsError(
        "Couldn't reach Backstage. Check your connection and try again.",
        0,
        'network',
        true,
      );
    }
    if (!res.ok) throw await toBulkError(res);
    if (res.status === 204) return undefined as T;
    try {
      return (await res.json()) as T;
    } catch {
      throw new BulkActionsError(
        'Backstage sent a response that could not be read. Try again.',
        res.status,
        'bad_response',
        true,
      );
    }
  }
}

const GATEWAY_STATUSES = new Set([502, 503, 504]);

async function toBulkError(res: Response): Promise<BulkActionsError> {
  const body = await res.json().catch(() => undefined);
  const error = body?.error ?? {};
  const code = typeof error.code === 'string' ? error.code : undefined;
  // A 404 without our envelope means this backend has no /bulk routes yet (before 1a ships).
  if (!code && res.status === 404) {
    return new BulkActionsError(
      "Bulk Actions isn't available here yet.",
      404,
      'bulk_unavailable',
      false,
    );
  }
  // Gateway errors from the load balancer arrive without our envelope.
  if (!code && GATEWAY_STATUSES.has(res.status)) {
    return new BulkActionsError(
      'Backstage is temporarily unavailable. Try again.',
      res.status,
      'gateway',
      true,
    );
  }
  return new BulkActionsError(
    typeof error.message === 'string' && error.message
      ? error.message
      : `The request failed (HTTP ${res.status}).`,
    res.status,
    code ?? 'unknown',
    error.retryable === true,
  );
}
